import { faCloudArrowUp, faFilePdf, faX } from '@fortawesome/pro-light-svg-icons';
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { DatePicker } from 'components/Form/DatePicker/DatePicker';
import { Input } from 'components/Form/Input/Input';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { PetVaccine } from 'types/Pet';

import { Tag } from 'components/UI/Tag/Tag';
import classes from './PetVaccineOption.module.scss';

interface PetVaccineOptionProps {
    vaccine: PetVaccine & { checked: boolean };
    onChange: (vaccine: PetVaccine & { checked: boolean }) => void;
}

export const PetVaccineOption: React.FC<PetVaccineOptionProps> = props => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);

    const isExpired = React.useMemo(() => {
        return props.vaccine.expiration ? props.vaccine.expiration.isBefore(moment()) : false;
    }, [props.vaccine.expiration]);

    const dateValue = React.useMemo(() => {
        return props.vaccine.expiration || null;
    }, [props.vaccine.expiration]);

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        props.onChange({ ...props.vaccine, checked });
    };

    const onDateChangeHandler = (date: Moment | null) => {
        const isExpired = date?.isBefore(moment());

        props.onChange({
            ...props.vaccine,
            status: isExpired ? 'Expired' : 'Active',
            expiration: date ?? undefined
        });
    };

    const fileChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange({
            ...props.vaccine,
            recordFileToUpload: event.target.files?.[0]
        });
    };

    const removeFileHandler = () => {
        props.onChange({
            ...props.vaccine,
            recordFileToUpload: undefined
        });
    };

    return (
        <div className={classes.root}>
            <Checkbox
                checked={props.vaccine.checked}
                label={props.vaccine.type?.name}
                className={classes.checkbox}
                onChange={onChangeHandler}
            />

            {isExpired && (
                <div>
                    <Tag>Expired</Tag>
                </div>
            )}

            <input type="file" hidden ref={fileInputRef} onChange={fileChangeHandler} />

            {props.vaccine.checked && (
                <div className={classes.actions}>
                    <DatePicker
                        label=""
                        value={dateValue}
                        open={isDatePickerOpen}
                        onOpen={() => setIsDatePickerOpen(true)}
                        onClose={() => setIsDatePickerOpen(false)}
                        InputProps={{
                            style: { width: 0, height: 0, opacity: 0 }
                        }}
                        inputLabelProps={{
                            shrink: false,
                            style: { marginLeft: 0 }
                        }}
                        renderInput={({ InputProps, ...params }) => (
                            <>
                                <Input
                                    {...params}
                                    InputProps={{
                                        ...InputProps,
                                        endAdornment: null,
                                        startAdornment: <FontAwesomeIcon icon={faCalendarDays} />
                                    }}
                                    InputLabelProps={{
                                        shrink: false
                                    }}
                                    className={classes.datePickerInput}
                                    onClick={() => setIsDatePickerOpen(true)}
                                    style={{
                                        width: 1,
                                        height: 1,
                                        opacity: 0,
                                        position: 'absolute',
                                        top: 0,
                                        left: 0
                                    }}
                                />

                                <div
                                    className={classes.button}
                                    onClick={() => setIsDatePickerOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faCalendarDays} />

                                    <span>
                                        {dateValue ? (
                                            dateValue.format('MM/DD/yyyy')
                                        ) : (
                                            <>
                                                Add Expiration Date
                                                <i>*</i>
                                            </>
                                        )}
                                    </span>
                                </div>
                            </>
                        )}
                        onChange={onDateChangeHandler}
                    />

                    {props.vaccine.recordFileToUpload ? (
                        <button
                            type="button"
                            className={clsx(classes.button, classes.fileButton)}
                            onClick={removeFileHandler}
                        >
                            <FontAwesomeIcon icon={faFilePdf} />

                            <span>{props.vaccine.recordFileToUpload?.name ?? 'Upload'}</span>

                            <FontAwesomeIcon icon={faX} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={classes.button}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FontAwesomeIcon icon={faCloudArrowUp} />

                            <span>Upload</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
